import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { ActualVsPredictedRow, CPTBRow, HierarchyLevelConst } from '../dtc-commerce-mmm-types';
import { AggregatedChannel, aggregateChannels } from '../hooks/useChannelAggregation';
import {
  formatCurrency,
  formatDollarsExact,
  formatPercentage,
  formatWholeNumber
} from '../utils/dtc-commerce-mmm-format';
import { mapeQuality, Quality, QualityConst } from '../utils/dtc-commerce-mmm-model-fit';
import { TagTone } from './channel-insights-constants';
import {
  DAYS_PER_YEAR_SHIFT,
  HeadlineTile,
  HeadlineTileKind,
  HighlightChip,
  MAX_SAME_RANGE_WEEKS,
  MAX_STRONG_STRETCH_MONTHS,
  MIN_EDGE_MONTH_WEEKS,
  MONTH_CONTEXT_MIN_WEEKS,
  PEAK_MIN_MULTIPLE,
  STRONG_RETURN_MIN_MULTIPLE,
  TilePeriod,
  TilePeriodType,
  TilePoint,
  WEEKLY_CUTOFF_WEEKS,
  WEEKS_PER_YEAR
} from './model-highlights-constants';
import { dayLabel, monthName, shiftDate } from './model-highlights-utils';

interface BuildHeadlineTilesParams {
  // The selected range's actualized rows (actualized.cptbT12M on the page).
  cptbWindow: CPTBRow[];
  // Every actualized row (actualized.cptb), where the year-earlier weeks are read from.
  cptbHistory: CPTBRow[];
  avpWindow: ActualVsPredictedRow[];
  isRevenue: boolean;
  mape: number;
  // The date picker's range, inclusive 'YYYY-MM-DD'.
  startDate: string;
  endDate: string;
}

interface WindowTotals {
  spend: number;
  attributed: number;
  effectiveSpend: number;
  roas: number;
  cpa: number;
}

interface PeriodBucket<T> {
  key: string;
  // Distinct weekly rows in the bucket.
  weeks: number;
  rows: T[];
}

interface BucketTotals {
  attributed: number;
  spend: number;
  effectiveSpend: number;
}

interface MediaBucket extends BucketTotals {
  key: string;
  // The same weeks a year earlier, with the tooltip header that names them.
  yearEarlier: { totals: BucketTotals; label: string } | undefined;
}

interface YearEarlierSource {
  weeks: Map<string, string>;
  rowsByWeek: Map<string, CPTBRow[]>;
}

interface TileContext {
  useWeekly: boolean;
  isRevenue: boolean;
  weeklyRows: number;
  totals: WindowTotals;
  buckets: MediaBucket[];
  channels: AggregatedChannel[];
}

const MS_PER_WEEK = 7 * 86_400_000;
const WEEKS_PER_MONTH = WEEKS_PER_YEAR / 12;

const NO_MEDIA_HEADLINE = 'No paid media ran in these dates.';

const ACCURACY_READOUT: Record<Quality, string> = {
  [QualityConst.STRONG]: ' Channel-level numbers can be read with confidence.',
  [QualityConst.MODERATE]: ' Read small gaps between channels with care.',
  [QualityConst.WEAK]: ' Read channel-level numbers as directional.'
};

const ACCURACY_CHIPS: Record<Quality, HighlightChip> = {
  [QualityConst.STRONG]: { label: 'Strong fit', tone: TagTone.POSITIVE },
  [QualityConst.MODERATE]: { label: 'Moderate fit', tone: TagTone.CAUTION },
  [QualityConst.WEAK]: { label: 'Weaker fit', tone: TagTone.NEGATIVE }
};

const parseDay = (date: string): Date => new Date(`${date}T00:00:00`);

const percentOf = (part: number, whole: number): string =>
  formatPercentage(whole > 0 ? (part / whole) * 100 : 0, 0);

// Where a bucket sits in a sentence: "in November" or "the week of March 2nd".
const bucketPhrase = (key: string, useWeekly: boolean): string =>
  useWeekly ? `the week of ${dayLabel(key, false)}` : `in ${monthName(key)}`;

const periodOf = (useWeekly: boolean): TilePeriodType =>
  useWeekly ? TilePeriod.WEEK : TilePeriod.MONTH;

// Tooltip headers: "June 2nd, 2025" or "June 2025".
const pointLabel = (key: string, useWeekly: boolean): string =>
  useWeekly ? dayLabel(key) : `${monthName(key)} ${key.slice(0, 4)}`;

// Weeks between the picker's start and end, the count the Executive Summary uses to choose
// weekly or monthly points. Falls back to the weekly rows before a range is set.
export const rangeWeeks = (startDate: string, endDate: string, weeklyRows: number): number => {
  if (!startDate || !endDate) return weeklyRows;
  return Math.round((parseDay(endDate).getTime() - parseDay(startDate).getTime()) / MS_PER_WEEK);
};

// 'the week of May 26th, 2025 through the week of May 25th, 2026'. Both dates always carry the year.
export const formatRangeLabel = (startDate: string, endDate: string): string => {
  if (!startDate || !endDate) return '';
  return `the week of ${dayLabel(startDate)} through the week of ${dayLabel(endDate)}`;
};

// A month at either edge holding only a stub of the range (one week of May, say) is left off the
// charts and sentences so it doesn't read as a real month. The totals keep it.
const trimEdgeMonths = <T>(buckets: PeriodBucket<T>[]): PeriodBucket<T>[] => {
  const kept = buckets.filter(
    (b, i) => !((i === 0 || i === buckets.length - 1) && b.weeks < MIN_EDGE_MONTH_WEEKS)
  );
  return kept.length > 0 ? kept : buckets;
};

// Weekly buckets keyed by date, or monthly buckets keyed by 'YYYY-MM', in date order.
export const bucketByPeriod = <T extends { date: string }>(
  rows: T[],
  useWeekly: boolean
): PeriodBucket<T>[] => {
  const groups = new Map<string, { dates: Set<string>; rows: T[] }>();
  rows.forEach(row => {
    const key = useWeekly ? row.date : row.date.slice(0, 7);
    const group = groups.get(key) ?? { dates: new Set<string>(), rows: [] };
    group.dates.add(row.date);
    group.rows.push(row);
    groups.set(key, group);
  });
  const sorted = [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, group]) => ({ key, weeks: group.dates.size, rows: group.rows }));
  return useWeekly ? sorted : trimEdgeMonths(sorted);
};

// The same totals the Executive Summary shows for the range.
const windowTotals = (rows: CPTBRow[]): WindowTotals => {
  const spend = rows.reduce((s, r) => s + r.rawSpend, 0);
  const attributed = rows.reduce((s, r) => s + r.effectiveNormalized, 0);
  const effectiveSpend = rows.reduce((s, r) => s + r.effectiveSpend, 0);
  return {
    spend,
    attributed,
    effectiveSpend,
    roas: effectiveSpend > 0 ? attributed / effectiveSpend : 0,
    cpa: attributed > 0 ? effectiveSpend / attributed : 0
  };
};

const groupByWeek = (rows: CPTBRow[]): Map<string, CPTBRow[]> => {
  const byWeek = new Map<string, CPTBRow[]>();
  rows.forEach(r => {
    const list = byWeek.get(r.date) ?? [];
    list.push(r);
    byWeek.set(r.date, list);
  });
  return byWeek;
};

// Each picked week paired with the week 52 weeks before it, the same shift the cards use. Every
// range up to a year compares, even under the cards' 8-week minimum: short ranges plot single
// weeks, where a year-earlier line still reads clearly. Longer ranges, and ranges whose
// year-earlier weeks aren't all in the data, draw no line, so none runs half-drawn.
const yearEarlierSource = (
  weeks: string[],
  cptbHistory: CPTBRow[]
): YearEarlierSource | undefined => {
  if (weeks.length === 0 || weeks.length > MAX_SAME_RANGE_WEEKS) return undefined;
  const rowsByWeek = groupByWeek(cptbHistory);
  const pairs = weeks.map((d): [string, string] => [d, shiftDate(d, -DAYS_PER_YEAR_SHIFT)]);
  if (!pairs.every(([, baseline]) => rowsByWeek.has(baseline))) return undefined;
  return { weeks: new Map(pairs), rowsByWeek };
};

// A bucket's weeks shifted a year back. A month keeps its own name a year earlier ("June 2024"),
// as the cards name their baseline; a week is named by its own year-earlier date. The month name
// is deliberately approximate: 52 weeks is 364 days, so a month's last week can line up with one
// starting a day or two into the next month (June 30th, 2025 with July 1st, 2024). Naming the
// real span would read "June–July 2024" on most months.
const yearEarlierBucket = (
  key: string,
  bucketRows: CPTBRow[],
  useWeekly: boolean,
  { weeks, rowsByWeek }: YearEarlierSource
): MediaBucket['yearEarlier'] => {
  const baselineWeeks = [...new Set(bucketRows.map(r => r.date))].flatMap(d => {
    const baseline = weeks.get(d);
    return baseline ? [baseline] : [];
  });
  return {
    totals: windowTotals(baselineWeeks.flatMap(d => rowsByWeek.get(d) ?? [])),
    label: useWeekly
      ? dayLabel(baselineWeeks[0])
      : `${monthName(key)} ${Number(key.slice(0, 4)) - 1}`
  };
};

const mediaBuckets = (
  rows: CPTBRow[],
  useWeekly: boolean,
  yearEarlier: YearEarlierSource | undefined
): MediaBucket[] =>
  bucketByPeriod(rows, useWeekly).map(({ key, rows: bucketRows }) => {
    const totals = windowTotals(bucketRows);
    return {
      key,
      attributed: totals.attributed,
      spend: totals.spend,
      effectiveSpend: totals.effectiveSpend,
      yearEarlier: yearEarlier && yearEarlierBucket(key, bucketRows, useWeekly, yearEarlier)
    };
  });

// The year-earlier fields of a tile point, read with the same measure as the point itself.
const yearEarlierPoint = (
  b: MediaBucket,
  valueOf: (totals: BucketTotals) => number
): Pick<TilePoint, 'yearEarlier' | 'yearEarlierLabel'> =>
  b.yearEarlier
    ? { yearEarlier: valueOf(b.yearEarlier.totals), yearEarlierLabel: b.yearEarlier.label }
    : {};

const largestBy = <T>(items: T[], valueOf: (item: T) => number): T | undefined =>
  items.reduce<T | undefined>(
    (best, item) => (best === undefined || valueOf(item) > valueOf(best) ? item : best),
    undefined
  );

// Revenue KPIs plot return per dollar; conversion KPIs plot cost per conversion. A bucket only
// counts toward the sentences when its value is defined (spend for ROAS, results for CPA).
const returnOf = (b: BucketTotals, isRevenue: boolean): number | null => {
  if (isRevenue) return b.effectiveSpend > 0 ? b.attributed / b.effectiveSpend : null;
  return b.attributed > 0 && b.effectiveSpend > 0 ? b.effectiveSpend / b.attributed : null;
};

const returnContext = (weeklyRows: number): string => {
  if (weeklyRows === 0) return '';
  if (weeklyRows < MONTH_CONTEXT_MIN_WEEKS) return `${weeklyRows}-week average`;
  return `${Math.round(weeklyRows / WEEKS_PER_MONTH)}-month average`;
};

interface ReturnPoint {
  key: string;
  index: number;
  value: number;
}

// The strong (or, for cost per conversion, cheapest) stretch: buckets well ahead of the range's
// average. One month, or one unbroken run of months, is named; scattered months name the best.
const strongStretchChip = (
  scored: ReturnPoint[],
  { useWeekly, isRevenue, totals }: TileContext
): HighlightChip | undefined => {
  const average = isRevenue ? totals.roas : totals.cpa;
  if (average <= 0) return undefined;
  const flagged = scored.filter(p =>
    isRevenue
      ? p.value >= STRONG_RETURN_MIN_MULTIPLE * average
      : p.value <= average / STRONG_RETURN_MIN_MULTIPLE
  );
  const best = largestBy(flagged, p => (isRevenue ? p.value : -p.value));
  if (!best) return undefined;

  const word = isRevenue ? 'Strongest' : 'Cheapest';
  const chip = (label: string): HighlightChip => ({ label, tone: TagTone.POSITIVE });
  if (useWeekly) return chip(`${word} week: ${dayLabel(best.key, false)}`);
  if (flagged.length === 1) return chip(`${word} in ${monthName(best.key)}`);

  const first = flagged[0];
  const last = flagged[flagged.length - 1];
  const isUnbrokenRun = last.index - first.index === flagged.length - 1;
  if (isUnbrokenRun && flagged.length <= MAX_STRONG_STRETCH_MONTHS)
    return chip(`${word} ${monthName(first.key)}–${monthName(last.key)}`);
  return chip(`${word} in ${monthName(best.key)}`);
};

const returnHeadline = (
  scored: ReturnPoint[],
  { useWeekly, isRevenue, totals }: TileContext
): string => {
  if (totals.effectiveSpend <= 0) return NO_MEDIA_HEADLINE;
  const low = largestBy(scored, p => -p.value);
  const high = largestBy(scored, p => p.value);
  // With one bucket, or every bucket level, there is no spread to describe, so the average reads.
  if (!low || !high || low.key === high.key) {
    return isRevenue
      ? `Every $1 of paid media returned ${formatDollarsExact(totals.roas)} in these dates.`
      : `Each conversion cost ${formatDollarsExact(totals.cpa)} of paid media in these dates.`;
  }
  const period = useWeekly ? 'Weekly' : 'Monthly';
  const metric = isRevenue ? 'returns' : 'cost per conversion';
  // For cost per conversion, the best bucket is the cheapest, so the sentence reads best to worst.
  return `${period} ${metric} ranged from ${formatDollarsExact(low.value)} ${bucketPhrase(low.key, useWeekly)} to ${formatDollarsExact(high.value)} ${bucketPhrase(high.key, useWeekly)}.`;
};

const returnTile = (ctx: TileContext): HeadlineTile => {
  const { useWeekly, isRevenue, weeklyRows, totals, buckets } = ctx;
  const points: TilePoint[] = buckets.map(b => ({
    key: b.key,
    label: pointLabel(b.key, useWeekly),
    value: returnOf(b, isRevenue) ?? 0,
    ...yearEarlierPoint(b, earlier => returnOf(earlier, isRevenue) ?? 0)
  }));
  const scored = buckets.flatMap((b, index) => {
    const value = returnOf(b, isRevenue);
    return value === null ? [] : [{ key: b.key, index, value }];
  });
  return {
    kind: HeadlineTileKind.RETURN,
    period: periodOf(useWeekly),
    title: isRevenue ? 'Return on ad spend' : 'Cost per acquisition',
    context: returnContext(weeklyRows),
    value: formatDollarsExact(isRevenue ? totals.roas : totals.cpa),
    unit: isRevenue ? 'per $1 spent' : 'per conversion',
    points,
    headline: returnHeadline(scored, ctx),
    chip: totals.effectiveSpend > 0 ? strongStretchChip(scored, ctx) : undefined
  };
};

// The biggest bucket by some measure, named only when it clearly stands above an average one.
const peakBucket = (
  buckets: MediaBucket[],
  valueOf: (b: MediaBucket) => number
): MediaBucket | undefined => {
  if (buckets.length === 0) return undefined;
  const average = buckets.reduce((s, b) => s + valueOf(b), 0) / buckets.length;
  const peak = largestBy(buckets, valueOf);
  if (!peak || average <= 0 || valueOf(peak) < PEAK_MIN_MULTIPLE * average) return undefined;
  return peak;
};

// "November peak" or "Peak: week of March 2nd". Shared by the results and spend tiles so the two
// read the same way.
const peakChip = (key: string, useWeekly: boolean): HighlightChip => ({
  label: useWeekly ? `Peak: week of ${dayLabel(key, false)}` : `${monthName(key)} peak`,
  tone: TagTone.NEUTRAL
});

const resultsTile = (ctx: TileContext, avpWindow: ActualVsPredictedRow[]): HeadlineTile => {
  const { useWeekly, isRevenue, totals, buckets, channels } = ctx;
  const kpiNoun = isRevenue ? 'revenue' : 'conversions';
  const totalActual = avpWindow.reduce((s, r) => s + r.actualKpi, 0);
  const top = largestBy(channels, ch => ch.effectiveNormalized);
  const peak = peakBucket(buckets, b => b.attributed);
  const hasResults = totals.attributed > 0 && top !== undefined;
  // Each sentence names its own measure, so it reads without the tile's title.
  const peakSentence = peak
    ? ` Paid media ${kpiNoun} peaked ${bucketPhrase(peak.key, useWeekly)}, at ${percentOf(peak.attributed, totals.attributed)} of the total.`
    : '';
  return {
    kind: HeadlineTileKind.RESULTS,
    period: periodOf(useWeekly),
    title: isRevenue ? 'Revenue attributed' : 'Conversions attributed',
    context:
      totalActual > 0 ? `${percentOf(totals.attributed, totalActual)} of total ${kpiNoun}` : '',
    value: isRevenue ? formatCurrency(totals.attributed) : formatWholeNumber(totals.attributed),
    unit: 'to paid media',
    points: buckets.map(b => ({
      key: b.key,
      label: pointLabel(b.key, useWeekly),
      value: b.attributed,
      ...yearEarlierPoint(b, earlier => earlier.attributed)
    })),
    headline: hasResults
      ? `${top.displayName} drove the most ${kpiNoun}, ${percentOf(top.effectiveNormalized, totals.attributed)} of all ${kpiNoun} from paid media.${peakSentence}`
      : `Paid media drove no attributed ${kpiNoun} in these dates.`,
    chip: hasResults && peak ? peakChip(peak.key, useWeekly) : undefined
  };
};

const spendTile = (ctx: TileContext): HeadlineTile => {
  const { useWeekly, totals, buckets, channels } = ctx;
  const topSpender = largestBy(channels, ch => ch.rawSpend);
  const peak = peakBucket(buckets, b => b.spend);
  // Each sentence names its own measure, so it reads without the tile's title.
  const peakSentence = peak
    ? ` Spending peaked ${bucketPhrase(peak.key, useWeekly)}, at ${percentOf(peak.spend, totals.spend)} of the total.`
    : '';
  const hasSpend = totals.spend > 0 && topSpender !== undefined;
  return {
    kind: HeadlineTileKind.SPEND,
    period: periodOf(useWeekly),
    title: 'Media spend',
    context:
      channels.length > 0
        ? `across ${conditionallyPluralizeWithCount(channels.length, 'channel')}`
        : '',
    value: formatCurrency(totals.spend),
    unit: 'invested',
    points: buckets.map(b => ({
      key: b.key,
      label: pointLabel(b.key, useWeekly),
      value: b.spend,
      ...yearEarlierPoint(b, earlier => earlier.spend)
    })),
    headline: hasSpend
      ? `${topSpender.displayName} received the most budget, ${percentOf(topSpender.rawSpend, totals.spend)} of all paid media spend.${peakSentence}`
      : NO_MEDIA_HEADLINE,
    chip: hasSpend && peak ? peakChip(peak.key, useWeekly) : undefined
  };
};

const accuracyTile = (
  avpWindow: ActualVsPredictedRow[],
  mape: number,
  useWeekly: boolean
): HeadlineTile => {
  const quality = mapeQuality(mape);
  const mapeText = mape.toFixed(1);
  return {
    kind: HeadlineTileKind.ACCURACY,
    period: periodOf(useWeekly),
    title: 'Model accuracy',
    context: 'MAPE',
    value: `±${mapeText}%`,
    unit: 'average miss',
    points: bucketByPeriod(avpWindow, useWeekly).map(({ key, rows }) => ({
      key,
      label: pointLabel(key, useWeekly),
      value: rows.reduce((s, r) => s + r.actualKpi, 0),
      predicted: rows.reduce((s, r) => s + r.expectedKpi, 0)
    })),
    headline: `On weeks it hadn't seen, the model was off by ${mapeText}% on average.${ACCURACY_READOUT[quality]}`,
    chip: ACCURACY_CHIPS[quality]
  };
};

/**
 * The four headline tiles: return, results, spend and accuracy for the selected range. Every
 * sentence and chip reads the same points its chart draws, so text and line always agree.
 */
export const buildHeadlineTiles = ({
  cptbWindow,
  cptbHistory,
  avpWindow,
  isRevenue,
  mape,
  startDate,
  endDate
}: BuildHeadlineTilesParams): HeadlineTile[] => {
  const weeks = [...new Set(cptbWindow.map(r => r.date))];
  const weeklyRows = weeks.length;
  const useWeekly = rangeWeeks(startDate, endDate, weeklyRows) < WEEKLY_CUTOFF_WEEKS;
  const ctx: TileContext = {
    useWeekly,
    isRevenue,
    weeklyRows,
    totals: windowTotals(cptbWindow),
    buckets: mediaBuckets(cptbWindow, useWeekly, yearEarlierSource(weeks, cptbHistory)),
    channels: aggregateChannels(cptbWindow, HierarchyLevelConst.CHANNEL)
  };
  return [
    returnTile(ctx),
    resultsTile(ctx, avpWindow),
    spendTile(ctx),
    accuracyTile(avpWindow, mape, useWeekly)
  ];
};
